import axios from 'axios';
import httpClient from './httpClient';
import {
  Provider,
  ProviderDetail,
  GetAllProvidersResponse,
  GetProviderByIdResponse,
  UpdateProviderKycPayload,
  UpdateProviderKycResponse,
  UpdateProviderStatusPayload,
  UpdateProviderStatusResponse,
  GetListingsByProviderIdResponse,
  Listing,
} from '../types/pppadminproviders-model';

/**
 * Fetches the full list of providers for the admin panel via
 * GET /provider/for-admin/get-all.
 * Returns the providers array on success, throws an Error on failure.
 */
export const getAllProviders = async (): Promise<Provider[]> => {
  try {
    const { data } = await httpClient.get<GetAllProvidersResponse>(
      '/provider/for-admin/get-all'
    );
    if (!data.success || !data.result) {
      throw new Error(data.message || 'Failed to fetch providers.');
    }
    return data.result.providers;
  } catch (err) {
    if (axios.isAxiosError<GetAllProvidersResponse>(err)) {
      throw new Error(err.response?.data?.message || 'Failed to fetch providers.');
    }
    throw err;
  }
};

/**
 * Fetches a single provider's full profile (including KYC fields) via
 * GET /provider/admin/:id.
 * Returns the ProviderDetail on success, throws an Error on failure.
 */
export const getProviderById = async (id: string): Promise<ProviderDetail> => {
  try {
    const { data } = await httpClient.get<GetProviderByIdResponse>(
      `/provider/admin/${id}`
    );
    if (!data.success || !data.result) {
      throw new Error(data.message || 'Failed to fetch provider details.');
    }
    return data.result;
  } catch (err) {
    if (axios.isAxiosError<GetProviderByIdResponse>(err)) {
      throw new Error(err.response?.data?.message || 'Failed to fetch provider details.');
    }
    throw err;
  }
};

/**
 * Approves, rejects, or requests changes on a provider's KYC via
 * PUT /provider/admin/:id/kyc.
 * Throws an Error with the API message on failure.
 */
export const updateProviderKyc = async (
  id: string,
  payload: UpdateProviderKycPayload
): Promise<void> => {
  try {
    const { data } = await httpClient.put<UpdateProviderKycResponse>(
      `/provider/admin/${id}/kyc`,
      payload
    );
    if (!data.success) {
      throw new Error(data.message || 'Failed to update KYC status.');
    }
  } catch (err) {
    if (axios.isAxiosError<UpdateProviderKycResponse>(err)) {
      throw new Error(err.response?.data?.message || 'Failed to update KYC status.');
    }
    throw err;
  }
};

/**
 * Activates or deactivates a provider's listing visibility via
 * PUT /provider/admin/:id/status.
 * Throws an Error with the API message on failure.
 */
export const updateProviderStatus = async (
  id: string,
  payload: UpdateProviderStatusPayload
): Promise<void> => {
  try {
    const { data } = await httpClient.put<UpdateProviderStatusResponse>(
      `/provider/admin/${id}/status`,
      payload
    );
    if (!data.success) {
      throw new Error(data.message || 'Failed to update provider status.');
    }
  } catch (err) {
    if (axios.isAxiosError<UpdateProviderStatusResponse>(err)) {
      throw new Error(err.response?.data?.message || 'Failed to update provider status.');
    }
    throw err;
  }
};



/**
 * Fetches all listings belonging to a provider via
 * GET /provider/listings/admin/:providerId.
 * Returns the listings array on success, throws an Error on failure.
 */
export const getListingsByProviderId = async (providerId: string): Promise<Listing[]> => {
  try {
    const { data } = await httpClient.get<GetListingsByProviderIdResponse>(
      `/provider/listings/admin/${providerId}`
    );
    if (!data.success || !data.result) {
      throw new Error(data.message || 'Failed to fetch listings.');
    }
    return data.result.listings;
  } catch (err) {
    if (axios.isAxiosError<GetListingsByProviderIdResponse>(err)) {
      throw new Error(err.response?.data?.message || 'Failed to fetch listings.');
    }
    throw err;
  }
};